/* ---------------------------------------------------------------------------
   The department's own words: what it is called, and what it calls things.

   A one-function file, and it earns its place. `AGENCY` used to be a constant in
   src/data/seed.ts — an object literal naming Nepal's Department of Roads,
   imported by the title bar, the sign-in page, the options screen, the guide and
   the printed inventory sheet. Nine files read it as a value, which is precisely
   what a value cannot be once there is more than one department: a constant
   evaluated at module load is the department the bundle was built for, not the
   one whose register is on screen.

   So it is a call now, and `agency()` rather than `active().agency` at the call
   sites for the same reason `t()` is not `CATALOGUES[current][key]`: the screens
   are full of it, and what they want to say is "the department", not "reach into
   the profile registry".

   NOTHING IS CACHED. The tenant can change within a session — a support session
   with `?tenant=`, an administrator on a shared machine — and the whole
   application repaints on every store update, so a memoised block would be one
   frame of the wrong letterhead. It is a map lookup and a property read.
   --------------------------------------------------------------------------- */

import { active } from './registry.ts';
import type { AgencyBlock, TenantBrand, Vocabulary } from './types.ts';

/** The department in force, as it names itself on a document. */
export function agency(): AgencyBlock {
  return active().agency;
}

/** The department's colours and emblems, for the sign-in page. */
export function brand(): TenantBrand {
  return active().brand;
}

/**
 * What this jurisdiction calls the things every register has.
 *
 * Here beside `agency()` and for the same reason: these are labels on
 * inventory-sheet rows, they change with the department, and a screen wants to
 * say `vocab().area` rather than reach into the profile registry. Deliberately
 * NOT passed through `t()` — see the header of src/data/locale.ts on why
 * inventory-sheet field names stay in the language the paper sheet prints them
 * in. These ARE those names.
 */
export function vocab(): Vocabulary {
  return active().vocab;
}
